import { Head, Link } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader, SectionDivider } from '@/components/landing/PageHeader'
import { FuturaLogo } from '@/components/landing/FuturaLogo'
import { Zap, Globe, Code, Users } from 'lucide-react'

const values = [
  {
    icon: Zap,
    title: 'Performance First',
    description:
      'Sub-100ms latency is not a target, it\'s a requirement. We leverage a global edge network to deliver the fastest realtime communication infrastructure available.',
  },
  {
    icon: Code,
    title: 'Developer Experience',
    description:
      'Simple REST APIs. No proprietary SDKs. Standard WebRTC. We believe great infrastructure should be easy to integrate, not a black box.',
  },
  {
    icon: Globe,
    title: 'Global Scale',
    description:
      'Built on a global edge network spanning 300+ cities worldwide. Your users get low-latency connections regardless of where they are.',
  },
  {
    icon: Users,
    title: 'Transparent Pricing',
    description:
      'No hidden fees, no surprise charges. Pay only for what you use with pricing up to 80% lower than legacy providers.',
  },
]

export default function About() {
  return (
    <>
      <Head>
        <title>About — KonexZero</title>
        <meta name="description" content="KonexZero makes realtime communication accessible to every developer. Voice, video, and screen sharing via a simple REST API." />
      </Head>
      <PageLayout>
        <div className="space-y-12">
          <PageHeader
            eyebrow="About"
            title="Building the Future of Realtime Communication"
            description="KonexZero provides developers with the simplest, fastest, and most affordable way to add voice, video, and screen sharing to any application."
          />

          <SectionDivider />

        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Our Mission</h2>
          <p className="text-muted-foreground leading-relaxed">
            Realtime communication should be accessible to every developer, not just teams with
            dedicated infrastructure budgets. Legacy providers like Agora and 100ms charge premium
            prices and lock developers into proprietary SDKs. We're building a better alternative.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            KonexZero is powered by a global edge network, which means you get
            enterprise-grade performance at a fraction of the cost. Our simple REST API means you
            can integrate realtime communication using the HTTP client you already know -- no
            proprietary SDKs, no vendor lock-in.
          </p>
        </section>

        <div className="grid gap-4 sm:grid-cols-2">
          {values.map((value) => {
            const Icon = value.icon
            return (
              <article key={value.title} className="surface-card surface-card-hover p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-foreground/[0.04] text-foreground/80">
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </div>
                  <h3 className="font-heading text-base font-semibold tracking-tight">
                    {value.title}
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {value.description}
                </p>
              </article>
            )
          })}
        </div>

        <SectionDivider />

        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">How It Works</h2>
          <p className="text-muted-foreground leading-relaxed">
            KonexZero acts as a signaling and media relay layer between your application and
            our edge infrastructure. Here's the flow:
          </p>
          <ol className="list-decimal pl-6 space-y-3 text-muted-foreground leading-relaxed">
            <li>
              <strong className="text-foreground">Create an application</strong> in your dashboard
              to get API credentials (App ID + API Key).
            </li>
            <li>
              <strong className="text-foreground">Join a room</strong> by calling our REST API.
              We create a WebRTC session and return connection details.
            </li>
            <li>
              <strong className="text-foreground">Publish media</strong> by sending your local
              SDP offer. We relay it to our edge network and return the answer.
            </li>
            <li>
              <strong className="text-foreground">Subscribe to others</strong> to receive their
              audio/video streams. Standard WebRTC negotiation, simplified.
            </li>
          </ol>
          <p className="text-muted-foreground leading-relaxed">
            Check out our{' '}
            <Link href="/docs" className="text-primary hover:underline">
              API Documentation
            </Link>{' '}
            for detailed integration guides and code examples.
          </p>
        </section>

        <SectionDivider />

        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">A Product of Futura Research</h2>
          <p className="text-muted-foreground leading-relaxed">
            KonexZero is built by{' '}
            <a
              href="https://www.futura-research.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Futura Research
            </a>
            , a technology company focused on building developer-first infrastructure for the
            modern web. We believe the best tools are the ones that get out of your way and let
            you ship faster.
          </p>
          <FuturaLogo size="md" />
        </section>

        <SectionDivider />

        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Get in Touch</h2>
          <p className="text-muted-foreground leading-relaxed">
            Have questions, want to discuss enterprise needs, or just want to chat? We'd love to
            hear from you.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              href="/contact"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground shadow-sm h-9 px-4 hover:bg-primary/90 transition-colors"
            >
              Contact Us
            </Link>
            <Link
              href="/sign_up"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background shadow-sm h-9 px-4 hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Start Building Free
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
    </>
  )
}
